import * as R from 'remeda';
import { z } from 'zod';
import {
  DateTimeSchema,
  objectIdRef,
  ObjectIdSchema,
  ObjectIdString,
  subDocument,
} from '../common.js';

export const FileUploadMimeType = z.enum([
  'image/png',
  'image/jpeg',
  'image/webp',
  'text/plain',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);
export type FileUploadMimeType = z.infer<typeof FileUploadMimeType>;

export const isAllowedUploadMimeType = R.isIncludedIn(FileUploadMimeType.options);

export const FileUploadStatus = z.enum(['uploading', 'saved', 'failed', 'expired']);
export type FileUploadStatus = z.infer<typeof FileUploadStatus>;

const SignedUrlData = z.object({
  url: z.string(),
  action: z.enum(['write', 'read']),
  expires: DateTimeSchema,
  headers: z.record(z.string(), z.string()).optional(),
});
export type SignedUrlData = z.infer<typeof SignedUrlData>;

export const FILE_UPLOAD_CATEGORIES = {
  user_upload: ['screenshot', 'ask_toro_attachment'],
  system_upload: ['ask_toro_export'],
} as const satisfies Record<string, string[]>;

export const FileUploadBaseCategory = z.enum(R.keys(FILE_UPLOAD_CATEGORIES));
export type FileUploadBaseCategory = z.infer<typeof FileUploadBaseCategory>;

export const SystemUploadSubCategory = z.enum(FILE_UPLOAD_CATEGORIES.system_upload);
export type SystemUploadSubCategory = z.infer<typeof SystemUploadSubCategory>;

export const UserUploadSubCategory = z.enum(FILE_UPLOAD_CATEGORIES.user_upload);
export type UserUploadSubCategory = z.infer<typeof UserUploadSubCategory>;

const CategoryPathUnion = z.union([
  z.templateLiteral([FileUploadBaseCategory.enum.user_upload, '/', UserUploadSubCategory]),
  z.templateLiteral([FileUploadBaseCategory.enum.system_upload, '/', SystemUploadSubCategory]),
]);
type CategoryPathUnion = z.infer<typeof CategoryPathUnion>;

// Flatten the union into a single enum so it works well with Mongoose
const fullCategoryList = [
  ...R.map(FILE_UPLOAD_CATEGORIES.user_upload, (str) => `user_upload/${str}` as const),
  ...R.map(FILE_UPLOAD_CATEGORIES.system_upload, (str) => `system_upload/${str}` as const),
] as const satisfies Array<CategoryPathUnion>;

export const FileUploadCategory = z.enum(fullCategoryList);
export type FileUploadCategory = z.infer<typeof FileUploadCategory>;

// TODO:
//  - Add a check to see if our GCS lifecycle rules match this and log an error if not
//  - Calculate `expiresAt` based on this TTL, add to the db model
export const FILE_EXPIRATION_DAYS_BY_CATEGORY = {
  'user_upload/screenshot': 30,
  'user_upload/ask_toro_attachment': 365,
  'system_upload/ask_toro_export': 365,
} as const satisfies Record<FileUploadCategory, number>;

export const IFileUpload = z.object({
  _id: ObjectIdSchema,
  mimeType: FileUploadMimeType,
  size: z.number().int().optional(),
  status: FileUploadStatus.default('uploading'),
  account: objectIdRef('Account'),
  user: objectIdRef('AuthUser').nullish(),
  chat: objectIdRef('LlmChat').nullish(),
  name: z.string().optional(),
  bucket: z.string(),
  category: FileUploadCategory.optional(),
  storagePath: z.string(),

  customContext: z.looseObject({}).nullish(),

  uploadUrl: subDocument(SignedUrlData).nullish(),
  readUrl: subDocument(SignedUrlData).nullish(),

  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema,
});
export type IFileUpload = z.infer<typeof IFileUpload>;

export const CreateUserUploadInput = IFileUpload.pick({
  mimeType: true,
  size: true,
  name: true,
  customContext: true,
})
  .required({ size: true })
  .safeExtend({
    account: ObjectIdString,
    user: ObjectIdString,
    chat: ObjectIdString.nullish(),
    category: UserUploadSubCategory,
  });
export type CreateUserUploadInput = z.input<typeof CreateUserUploadInput>;

export const CreateFileUploadResult = IFileUpload.pick({
  uploadUrl: true,
  mimeType: true,
  size: true,
  status: true,
  name: true,
}).safeExtend({ id: ObjectIdString, uploadUrl: SignedUrlData });
export type CreateFileUploadResult = z.infer<typeof CreateFileUploadResult>;
